import {forkJoin, Observable, of} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {catchError, map} from 'rxjs/operators';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ClassTransformer, Exclude} from 'class-transformer';
import * as gql from 'gql-query-builder';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {BaseService} from './base-service';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {PagedModel} from '../../api/model/paged-model';
import {Metadata, MetadataId, MetadataType, MetadataTypeId, MetadataUnit} from '@app/graphql-typings/graphql';
import {MetadataTypeService} from './metadata-type.service';
import {MetadataUnitService} from './metadata-unit.service';
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import {MessageTranslateService} from '@shared/services';
import {StringUtils} from '@shared/util/general/string-utils';

export class MetadataSearchTerm {
  code?: string;
  metadata_ids?: MetadataId[];
  name?: string;
  unit_id?: number;
  type_id?: MetadataTypeId;
  type_ids?: MetadataTypeId[];
  shared?: boolean;
  commissionable?: boolean;
  system?: boolean;
}

export interface MetadataUsageCount {
  favourites: number;
  rules: number;
  templates: number;
}

export interface MetadataExtended extends Metadata {
  usageCounts?: MetadataUsageCount;
}


export class MetadataErrorResponse {
  constructor(public message: string) {
  }
}

export class MetadataImpl implements Metadata {
  commissionable?: boolean;
  system?: boolean;
  metadata_id: number;
  code: string;
  name: string;
  shared: boolean;
  type_id: number;
  @Exclude({toPlainOnly: true})
  type: MetadataType;
  unit_id: number;
  @Exclude({toPlainOnly: true})
  unit: MetadataUnit;
  customer_id: number;
}

export const metadataResolver: ResolveFn<Metadata> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<Metadata> => {
  return inject(MetadataService).getMetadatum(StringUtils.stringToNumber(route.paramMap.get('id')));
};

@Injectable()
export class MetadataService extends BaseService {

  public static readonly modelMapping = {Metadata: MetadataImpl};

  private readonly DATA_KEY = 'metadata';
  private readonly DATA_KEY_PLURAL = this.DATA_KEY;
  private readonly URL_PART = '/' + this.DATA_KEY_PLURAL;
  private readonly METADATA_STILL_REFERENCED_STATUS_CODE = 409;
  private readonly DEFAULT_PAGE_SIZE = 2000;
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private translateService: MessageTranslateService,
              private classTransformer: ClassTransformer,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(MetadataService.name);
  }

  getMetadatum(id: number): Observable<Metadata> {
    return this.getMetadataByIds([id]).pipe(map((metadata: Metadata[]) => CollectionUtils.only(metadata)));
  }

  getMetadata(pageInfo: PageInfo, searchTerm?: MetadataSearchTerm): Observable<PagedModel<Metadata[]>> {
    const query = gql.query({
      operation: 'metadata',
      fields: ['metadata_id', 'code', 'name', 'type_id', 'unit_id', 'shared', 'customer_id', 'system',
        {type: ['type_id', 'type', 'type_code', 'shared', 'customer_id']},
        {unit: ['unit_id', 'unit', 'shared', 'customer_id']}],
      variables: {
        metadata_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        name: {type: GRAPHQL_TYPES.STRING},
        unit_id: {type: GRAPHQL_TYPES.INTEGER},
        type_id: {type: GRAPHQL_TYPES.INTEGER},
        type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        shared: {type: GRAPHQL_TYPES.BOOLEAN},
        commissionable: {type: GRAPHQL_TYPES.BOOLEAN, value: true},
        system : {type: GRAPHQL_TYPES.BOOLEAN},
        code: {type: GRAPHQL_TYPES.STRING},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    });

    return this.httpUtilityService.graphQL<Metadata[]>(query, this.DATA_KEY_PLURAL, Object.assign({start_index: pageInfo.first, limit: pageInfo.rows}, searchTerm))
      .pipe(map(result => {
        const modelType = MetadataService.modelMapping['Metadata'];
        return {
          data: this.classTransformer.plainToInstance(modelType, result.data, {
            targetMaps: [{
              target: modelType,
              properties: {
                unit: MetadataUnitService.modelMapping['MetadataUnit'],
                type: MetadataTypeService.modelMapping['MetadataType']
              }
            }]
          }),
          count: result.count
        };
      }));
  }


  getAllMetadata(searchTerm?: MetadataSearchTerm): Observable<Metadata[]> {
    return this.httpUtilityService.pageUntilComplete((pageInfo) => this.getMetadata(pageInfo, searchTerm), {first: 0, rows: this.DEFAULT_PAGE_SIZE});
  }

  getMetadataByIds(ids: number[]): Observable<Metadata[]> {
    return this.httpUtilityService.requestPartitioned(partitionIds => this.getAllMetadata({commissionable: null, metadata_ids: partitionIds}), ids, this.DEFAULT_PAGE_SIZE);
  }

  getMetadataUsageCounts(metadataId: number): Observable<MetadataUsageCount> {
    const params = new HttpParams().set('start_index', '0').set('limit', '1').set('metadata_id', metadataId.toString());
    const options = {headers: this.getHeaders(), params: params};
    return forkJoin([
      this.http.get(this.getGateway() + '/favourites', options),
      this.http.get(this.getGateway() + '/tasks', options),
      this.http.get(this.getGateway() + '/tasks/templates', options),
    ]).pipe(map(responseArray => ({
      favourites: this.httpUtilityService.extractRecordCountFromResponse(responseArray[0]),
      rules: this.httpUtilityService.extractRecordCountFromResponse(responseArray[1]),
      templates: this.httpUtilityService.extractRecordCountFromResponse(responseArray[2])
    })));
  }

  createMetadata(metadata: Metadata): Observable<Metadata> {
    const m = this.prepareObjectForApi(metadata);
    const options = {headers: this.getHeaders()};
    return this.http.post(this.getGateway() + this.URL_PART, m, options).pipe(map(response => {
      const data = this.httpUtilityService.extractResponseData(response, this.DATA_KEY);
      return this.classTransformer.plainToInstance(MetadataService.modelMapping['Metadata'], data);
    }));
  }

  updateMetadata(metadata: Metadata): Observable<Metadata> {
    const m = this.prepareObjectForApi(metadata);
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + this.URL_PART + '/' + metadata.metadata_id, m, options).pipe(map(response => {
      const data = this.httpUtilityService.extractResponseData(response, this.DATA_KEY);
      return this.classTransformer.plainToInstance(MetadataService.modelMapping['Metadata'], data);
    }));
  }

  changeSharingMetadata(metadata: Metadata): Observable<Metadata> {
    metadata.shared = !metadata.shared;
    return this.updateMetadata(metadata);
  }

  deleteMetadata(metadata: Metadata): Observable<number | MetadataErrorResponse> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + this.URL_PART + '/' + metadata.metadata_id, options)
      .pipe(
        map(response => this.httpUtilityService.extractResponseData(response, this.DATA_KEY)['metadata_id']),
        catchError((error: any) => {
          if (error.status === this.METADATA_STILL_REFERENCED_STATUS_CODE) {
            return of(new MetadataErrorResponse(
              this.translateService.instant('Cannot delete metadata as it is still referenced by favourites', {name: metadata.name})
            ));
          } else {
            throw error;
          }
        }));
  }

  private prepareObjectForApi(metadata: Metadata): Object {
    return this.classTransformer.instanceToPlain(metadata);
  }
}
